import { useEffect, useRef, useState } from "react";
import ArrowDown from "@/assetsV3/marketplace/ArrowDown";
import SolanaIcon from "@/assetsV3/Wallet/SolanaIcon";
import useSolanaTransaction from "@/callbacks/useSolanaTransaction";
import { IGCCHoldings } from "@/types/IGCCHoldings";
import { useAppSelector } from "@/redux/hooks";
import { getGCCHoldings } from "@/services/getGCCHoldings";
import PortfolioSkeleton from "@/components/Wallet/Skeletons/PortfolioSkeleton";
import Image from "next/image";
import getRandomPfp from "@/utils/getRandomPfp";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import GccSelectionSheet from "../BottomSheets/GccSelectionSheet";

interface Props {
  setSelectedGcc: (val: IGCCHoldings) => void;
  selectedGcc: IGCCHoldings | undefined;
  isEnableWalletSelection: boolean;
}

const PickGccAccordion: React.FC<Props> = ({
  selectedGcc,
  setSelectedGcc,
  isEnableWalletSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [holdings, setHoldings] = useState<IGCCHoldings[]>([]);
  const user = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { fromLamports } = useSolanaTransaction();
  const _currentWallet = useAppSelector((state) => state.wallet);

  const getData = async () => {
    setIsLoading(true); // Start loader
    try {
      const gccHoldings = await getGCCHoldings(0, _currentWallet.address);
      console.log("gccHoldings", gccHoldings);
      setHoldings(gccHoldings.filter(holding => holding.tokens !== holding.tokens_onmarket));
    } catch (error) {
      console.error("Error fetching data:", error); // Handle errors here
    } finally {
      setIsLoading(false); // Stop loader
    }
  };

  useEffect(() => {
    user && _currentWallet && _currentWallet.address !== "" && getData();
  }, [user, _currentWallet]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen); // Toggle accordion open/close
  };

  // Close accordion if click happens outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the accordion if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);


  return (
    <div
      className="relative rounded-[8px] w-full"
      ref={dropdownRef}
    >
      {/* Accordion Button */}
      <button
        onClick={() => {
          if (isEnableWalletSelection) {
            toggleAccordion();
          }
        }}
        className={`flex justify-between w-full py-[12px] px-[18px] h-[46px] text-left  font-normal text-outext-sub border-[1px] border-outstate-grayborder focus:outline-none ${!isEnableWalletSelection ? "opacity-50" : ""
          } ${isOpen ? "rounded-x-[2px] rounded-t-[8px]" : "rounded-[8px]"}`}
      >
        {selectedGcc ? (
          <div className="w-full cursor-pointer transition-all">
            <div className="flex whitespace-nowrap justify-between items-center w-full gap-4">
              <div className="flex gap-2 items-center">
                <Image
                  height={24}
                  width={24}
                  src={
                    selectedGcc.subject.profilePic
                      ? selectedGcc.subject.profilePic
                      : getRandomPfp()
                  }
                  alt="test"
                  className="rounded-full"
                  onError={async ({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = getRandomPfp();
                  }}
                />
                <div className="grid grid-flow-row mr-1">
                  <div className="flex flex-row gap-1 items-center text-clip overflow-hidden text-nowrap">
                    <span className="text-base text-outext-main leading-[1.375rem]">
                      {selectedGcc.subject.displayName}
                    </span>
                    <span className="text-base  text-outext-sub">
                      @{selectedGcc.subject.handle}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <p className="text-sm text-outext-disabled font-normal">
                  {fromLamports(selectedGcc.tokens) -
                    fromLamports(selectedGcc.tokens_onmarket)}{" "}
                  {fromLamports(selectedGcc.tokens) -
                    fromLamports(selectedGcc.tokens_onmarket) >
                    1
                    ? "Chips"
                    : "Chip"}
                </p>
                <span>
                  <ArrowDown className="fill-outicon-sub"/>
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between w-full">
            <span className="text-sm font-medium text-outext-sub">Pick Chip</span>
            <span>
              <ArrowDown className="fill-outicon-sub"/>
            </span>
          </div>
        )}
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <>
          <GccSelectionSheet
            handleGccSheetChange={setIsOpen}
            selectedGcc={selectedGcc}
            setSelectedGcc={setSelectedGcc}
            gccSheetState={isOpen}
          />
          <div className="max-sm:hidden absolute left-0 overflow-hidden bg-outbackground-main right-0 border-x-[1px] border-b-[1px] rounded-b-[8px] border-outstate-grayborder  shadow-lg z-10">
            <ul className="max-h-[110px] overflow-y-scroll no-scrollbar">
              {isLoading ? (
                [1, 2, 3, 4].map((_, index) => (
                  <SkeletonTheme
                    key={index}
                    baseColor="#373737"
                    highlightColor="#C0C0C0"
                  >
                    <div
                      key={index}
                      className="py-3 px-[18px] gap-3 w-full cursor-pointer transition-all hover:bg-outstroke-soft"
                    >
                      <div className="flex justify-between whitespace-nowrap items-center w-full gap-4">
                        <div className="flex gap-2">
                          <Skeleton
                            count={1}
                            width={"24px"}
                            height={"24px"}
                            style={{ borderRadius: "100%" }}
                          />
                          <span className="text-base text-outext-main leading-[1.375rem] truncate group-hover:truncate overflow-hidden">
                            <Skeleton
                              count={1}
                              width={"75px"}
                              height={"20px"}
                              style={{ borderRadius: "8px" }}
                            />
                          </span>
                          <span className="text-base -ml-1 text-outext-sub">
                            <Skeleton
                              count={1}
                              width={"75px"}
                              height={"20px"}
                              style={{ borderRadius: "8px" }}
                            />
                          </span>
                        </div>
                        <p className="text-sm text-outext-disabled font-normal">
                          <Skeleton
                            count={1}
                            width={"75px"}
                            height={"20px"}
                            style={{ borderRadius: "8px" }}
                          />
                        </p>
                      </div>
                    </div>
                  </SkeletonTheme>
                ))
              ) : holdings.length > 0 ? (
                holdings.map((holding, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setSelectedGcc(holding);
                      setIsOpen(false);
                    }}
                    className={`py-3 px-[18px] gap-3 w-full cursor-pointer transition-all hover:bg-outstroke-soft ${selectedGcc === holding ? "bg-outstroke-soft" : ""
                      }`}
                  >
                    <div className="flex justify-between whitespace-nowrap items-center w-full gap-4">
                      <div className="flex gap-2 items-center">
                        <Image
                          height={24}
                          width={24}
                          src={
                            holding.subject.profilePic
                              ? holding.subject.profilePic
                              : getRandomPfp()
                          }
                          alt="test"
                          className="rounded-full"
                          onError={async ({ currentTarget }) => {
                            currentTarget.onerror = null; // prevents looping
                            currentTarget.src = getRandomPfp();
                          }}
                        />
                        <div className="grid grid-flow-row mr-1">
                          <div className="flex flex-row gap-x-2 items-center text-clip overflow-hidden text-nowrap">
                            <span className="text-base text-outext-main leading-[1.375rem]">
                              {holding.subject.displayName}
                            </span>
                            <span className="text-base -ml-1 text-outext-sub">
                              @{holding.subject.handle}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-base text-outext-disabled font-normal">
                        {fromLamports(holding.tokens) -
                          fromLamports(holding.tokens_onmarket)}{" "}
                        {fromLamports(holding.tokens) -
                          fromLamports(holding.tokens_onmarket) >
                          1
                          ? "Chips"
                          : "Chip"}
                      </p>
                    </div>
                  </li>
                ))
              ) : (
                <div className="flex items-center justify-center">
                  <p className="text-base text-outext-sub">No Data</p>
                </div>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default PickGccAccordion;
